/**
 * ============================================================
 * ⚠️  PROTECTED FILE - DO NOT MODIFY WITHOUT APPROVAL  ⚠️
 * ============================================================
 * This is the Property Hub page shown at /sales/property/{caseId}
 * Contains left sidebar with: Dashboard, Property Details, Marketing,
 * Photos, Floorplans, EPC, IDs, Brochures, Documents, Parties,
 * Viewings, Offers, Sale Progression
 * 
 * Layout and functionality have been approved by the client.
 * Any changes require explicit approval.
 * ============================================================
 */

import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  ArrowLeft, Home, PoundSterling, Bed, Bath, Car, Square,
  MapPin, Calendar, User, Building2, FileText, Camera, 
  ChevronRight, Loader2, CheckCircle, Clock, Users, Briefcase,
  Image, Layout, Zap, Key, BookOpen, Eye, HandCoins, TrendingUp,
  Upload, X, Plus, ExternalLink, Mail, MessageSquare, Globe,
  Wifi, Flame, Droplets, AlertCircle, Trash2, Save, Circle
} from "lucide-react";
import { API } from "@/App";

const SalesPropertyPage = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [caseData, setCaseData] = useState(null);
  const [parties, setParties] = useState([]);
  const [activeSection, setActiveSection] = useState("details");
  const [photos, setPhotos] = useState([]);
  const [floorplans, setFloorplans] = useState([]);
  const [uploading, setUploading] = useState(false);
  const photoInputRef = useRef(null);
  const floorplanInputRef = useRef(null);
  const epcInputRef = useRef(null);
  const token = localStorage.getItem("token");
  const userStr = localStorage.getItem("user");
  const user = userStr ? JSON.parse(userStr) : null;
  
  // Editable marketing fields
  const [marketingData, setMarketingData] = useState({
    on_market: false,
    summary_description: "",
    full_description: "",
    headline: "",
    marketing_tag: "",
    virtual_tour_1: "",
    virtual_tour_2: "",
    website_link: "",
    rightmove_link: "",
    zoopla_link: "",
    onthemarket_link: "",
    key_features: [],
    restrictions: [],
    electricity_supply: "",
    water_supply: "",
    broadband: "",
    heating: "",
    sewerage: ""
  });
  const [newFeature, setNewFeature] = useState("");
  const [newRestriction, setNewRestriction] = useState("");
  
  // Roles that can see the sales fee (staff + seller only)
  const canSeeSalesFee = user?.role && ['director', 'admin', 'sales_agent', 'sales_negotiator', 'seller'].includes(user.role);

  useEffect(() => {
    fetchCaseData();
  }, [caseId]);

  const fetchCaseData = async () => {
    try {
      const response = await axios.get(`${API}/sales/cases/${caseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const caseInfo = response.data.case;
      
      // Safety check: ensure we have valid case data
      if (!caseInfo) {
        throw new Error("Case data is empty");
      }
      
      setCaseData(caseInfo);
      setParties(response.data.parties || []);
      
      // Initialize marketing data from case with safe defaults
      setMarketingData({
        on_market: caseInfo.on_market || false,
        summary_description: caseInfo.summary_description || "",
        full_description: caseInfo.full_description || caseInfo.description || "",
        headline: caseInfo.headline || "",
        marketing_tag: caseInfo.marketing_tag || "",
        virtual_tour_1: caseInfo.virtual_tour_1 || "",
        virtual_tour_2: caseInfo.virtual_tour_2 || "",
        website_link: caseInfo.website_link || "",
        rightmove_link: caseInfo.rightmove_link || "",
        zoopla_link: caseInfo.zoopla_link || "",
        onthemarket_link: caseInfo.onthemarket_link || "",
        key_features: Array.isArray(caseInfo.key_features) ? caseInfo.key_features : [],
        restrictions: Array.isArray(caseInfo.restrictions) ? caseInfo.restrictions : [],
        electricity_supply: caseInfo.electricity_supply || "",
        water_supply: caseInfo.water_supply || "",
        broadband: caseInfo.broadband || "",
        heating: caseInfo.heating || "",
        sewerage: caseInfo.sewerage || ""
      });
      
      // Load photos and floorplans with array safety
      setPhotos(Array.isArray(caseInfo.photos) ? caseInfo.photos : []);
      setFloorplans(Array.isArray(caseInfo.floorplans) ? caseInfo.floorplans : []);
    } catch (error) {
      console.error("Error loading case:", error);
      
      // Check if the ID looks like an instruction ID and redirect
      if (caseId?.startsWith('sinst_') || caseId?.startsWith('inst_')) {
        console.log("Redirecting to instruction hub page");
        navigate(`/sales/property-hub/${caseId}`, { replace: true });
        return;
      }
      
      // Check if it's a 404 - the case might be in property_instructions
      if (error.response?.status === 404) {
        // Try to fetch as property instruction
        try {
          const instrResponse = await axios.get(`${API}/instructions/sales/${caseId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (instrResponse.data) {
            // Redirect to instruction hub page
            navigate(`/sales/property-hub/${caseId}`, { replace: true });
            return;
          }
        } catch {
          // Not found as instruction either, show error
        }
      }
      
      toast.error("Failed to load property data");
    } finally {
      setLoading(false);
    }
  };

  // Save marketing details
  const saveMarketingDetails = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/sales/cases/${caseId}`, {
        ...marketingData
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Marketing details saved");
    } catch (error) {
      toast.error("Failed to save marketing details");
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  // Add feature
  const addFeature = () => {
    if (newFeature.trim()) {
      setMarketingData(prev => ({
        ...prev,
        key_features: [...prev.key_features, newFeature.trim()]
      }));
      setNewFeature("");
    }
  };

  // Remove feature
  const removeFeature = (index) => {
    setMarketingData(prev => ({
      ...prev,
      key_features: prev.key_features.filter((_, i) => i !== index)
    }));
  };

  // Add restriction
  const addRestriction = () => {
    if (newRestriction.trim()) {
      setMarketingData(prev => ({
        ...prev,
        restrictions: [...prev.restrictions, newRestriction.trim()]
      }));
      setNewRestriction("");
    }
  };

  // Remove restriction
  const removeRestriction = (index) => {
    setMarketingData(prev => ({
      ...prev,
      restrictions: prev.restrictions.filter((_, i) => i !== index)
    }));
  };

  // Handle file upload for photos
  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploading(true);
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    formData.append('type', 'photo');
    
    try {
      const response = await axios.post(`${API}/sales/cases/${caseId}/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setPhotos(prev => [...prev, ...response.data.files]);
      toast.success(`${files.length} photo(s) uploaded`);
    } catch (error) {
      toast.error("Failed to upload photos");
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  // Handle file upload for floorplans
  const handleFloorplanUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploading(true);
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    formData.append('type', 'floorplan');
    
    try {
      const response = await axios.post(`${API}/sales/cases/${caseId}/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setFloorplans(prev => [...prev, ...response.data.files]);
      toast.success(`${files.length} floorplan(s) uploaded`);
    } catch (error) {
      toast.error("Failed to upload floorplans");
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  // Handle EPC document upload
  const handleEpcUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', 'epc');
    
    try {
      const response = await axios.post(`${API}/sales/cases/${caseId}/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setCaseData(prev => ({ ...prev, epc_document: response.data.file }));
      toast.success("EPC document uploaded");
    } catch (error) {
      toast.error("Failed to upload EPC");
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  // Delete photo
  const deletePhoto = async (photoUrl, index) => {
    try {
      await axios.delete(`${API}/sales/cases/${caseId}/photo`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { photo_url: photoUrl }
      });
      setPhotos(prev => prev.filter((_, i) => i !== index));
      toast.success("Photo deleted");
    } catch (error) {
      // If endpoint doesn't exist, just remove locally
      setPhotos(prev => prev.filter((_, i) => i !== index));
    }
  };

  // Delete floorplan
  const deleteFloorplan = async (url, index) => {
    try {
      await axios.delete(`${API}/sales/cases/${caseId}/floorplan`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { floorplan_url: url }
      });
      setFloorplans(prev => prev.filter((_, i) => i !== index));
      toast.success("Floorplan deleted");
    } catch (error) {
      setFloorplans(prev => prev.filter((_, i) => i !== index));
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(amount || 0);
  };

  const getStatusColor = (status) => {
    const colors = {
      'draft': 'bg-slate-100 text-slate-700',
      'active': 'bg-blue-100 text-blue-700',
      'sstc': 'bg-amber-100 text-amber-700',
      'exchanged': 'bg-emerald-100 text-emerald-700',
      'completed': 'bg-green-100 text-green-700',
      'sold': 'bg-green-100 text-green-700',
      'withdrawn': 'bg-red-100 text-red-700'
    };
    return colors[status] || 'bg-slate-100 text-slate-700';
  };

  const menuItems = [
    { id: "details", label: "Property Details", icon: Home },
    { id: "parties", label: "Parties", icon: Users },
    { id: "milestones", label: "Sales Progression", icon: CheckCircle },
    { id: "documents", label: "Documents", icon: FileText },
  ];

  // Property Menu items
  const propertyMenuItems = [
    { id: "dashboard", label: "Dashboard", icon: Layout },
    { id: "details", label: "Property Details", icon: Home },
    { id: "marketing", label: "Marketing Details", icon: TrendingUp },
    { id: "photos", label: "Photos", icon: Image },
    { id: "floorplans", label: "Floorplans", icon: Layout },
    { id: "epc", label: "EPC", icon: Zap },
    { id: "ids", label: "IDs", icon: Key },
    { id: "brochures", label: "Brochures", icon: BookOpen },
    { id: "documents", label: "Documents", icon: FileText },
    { id: "parties", label: "Parties", icon: Users },
  ];

  // Sale Menu items
  const saleMenuItems = [
    { id: "viewings", label: "Viewings", icon: Eye },
    { id: "offers", label: "Offers", icon: HandCoins },
    { id: "progression", label: "Sale Progression", icon: CheckCircle },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!caseData) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <Home className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Property Not Found</h2>
            <p className="text-slate-500 mb-4">This property could not be found.</p>
            <Button onClick={() => navigate('/sales')}>Back to Sales</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const seller = parties.find(p => p.role === "seller");
  const buyer = parties.find(p => p.role === "buyer");
  const sellerSolicitor = parties.find(p => p.role === "seller_solicitor");
  const buyerSolicitor = parties.find(p => p.role === "buyer_solicitor");

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/sales')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="h-6 w-px bg-slate-200" />
              <div>
                <h1 className="text-lg font-semibold text-slate-800">{caseData.address_line1}</h1>
                <p className="text-sm text-slate-500">{caseData.postcode}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Badge className={`text-sm px-3 py-1 ${getStatusColor(caseData.status)}`}>
                {caseData.status === 'completed' ? 'SOLD' : caseData.status?.toUpperCase()}
              </Badge>
              <Badge className="bg-emerald-100 text-emerald-700 text-lg px-4 py-1">
                {formatCurrency(caseData.offer_price || caseData.asking_price)}
              </Badge>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex gap-6">
          {/* Sidebar Menu */}
          <div className="w-56 flex-shrink-0">
            <Card className="sticky top-24">
              <CardContent className="p-3">
                {/* Property Menu */}
                <div className="mb-4">
                  <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-3 mb-2">Property menu</h3>
                  <nav className="space-y-0.5">
                    {propertyMenuItems.map((item) => {
                      const Icon = item.icon;
                      const isActive = activeSection === item.id;
                      return (
                        <button
                          key={item.id}
                          onClick={() => setActiveSection(item.id)}
                          data-testid={`menu-${item.id}`}
                          className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                            isActive
                              ? "bg-blue-50 text-blue-700 font-medium"
                              : "text-slate-600 hover:bg-slate-50"
                          }`}
                        >
                          <Icon className="w-4 h-4" />
                          <span className="text-sm">{item.label}</span>
                        </button>
                      );
                    })}
                  </nav>
                </div>
                
                {/* Sale Menu */}
                <div>
                  <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-3 mb-2">Sale menu</h3>
                  <nav className="space-y-0.5">
                    {saleMenuItems.map((item) => {
                      const Icon = item.icon;
                      const isProgression = item.id === "progression";
                      const isActive = activeSection === item.id;
                      return (
                        <button
                          key={item.id}
                          onClick={() => {
                            if (isProgression) {
                              navigate(`/sales/case/${caseId}/hub`);
                            } else {
                              setActiveSection(item.id);
                            }
                          }}
                          data-testid={`menu-${item.id}`}
                          className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                            isActive
                              ? "bg-blue-50 text-blue-700 font-medium"
                              : "text-slate-600 hover:bg-slate-50"
                          }`}
                        >
                          <Icon className="w-4 h-4" />
                          <span className="text-sm">{item.label}</span>
                          {isProgression && <ChevronRight className="w-4 h-4 ml-auto text-slate-400" />}
                        </button>
                      );
                    })}
                  </nav>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {/* Property Details Section */}
            {activeSection === "details" && (
              <div className="space-y-6">
                {/* Property Overview Card */}
                <Card>
                  <CardContent className="p-6">
                    <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <Home className="w-5 h-5 text-blue-600" />
                      Property Overview
                    </h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="p-4 bg-slate-50 rounded-lg text-center">
                        <Bed className="w-6 h-6 text-slate-400 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-slate-800">{caseData.bedrooms || '-'}</p>
                        <p className="text-sm text-slate-500">Bedrooms</p>
                      </div>
                      <div className="p-4 bg-slate-50 rounded-lg text-center">
                        <Bath className="w-6 h-6 text-slate-400 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-slate-800">{caseData.bathrooms || '-'}</p>
                        <p className="text-sm text-slate-500">Bathrooms</p>
                      </div>
                      <div className="p-4 bg-slate-50 rounded-lg text-center">
                        <Building2 className="w-6 h-6 text-slate-400 mx-auto mb-2" />
                        <p className="text-lg font-bold text-slate-800">{caseData.property_type || '-'}</p>
                        <p className="text-sm text-slate-500">Type</p>
                      </div>
                      <div className="p-4 bg-slate-50 rounded-lg text-center">
                        <MapPin className="w-6 h-6 text-slate-400 mx-auto mb-2" />
                        <p className="text-lg font-bold text-slate-800">{caseData.tenure || '-'}</p>
                        <p className="text-sm text-slate-500">Tenure</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Pricing Card */}
                <Card>
                  <CardContent className="p-6">
                    <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <PoundSterling className="w-5 h-5 text-emerald-600" />
                      Pricing
                    </h2>
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <label className="text-sm text-slate-500">Asking Price</label>
                        <p className="text-2xl font-bold text-slate-800">{formatCurrency(caseData.asking_price)}</p>
                      </div>
                      <div>
                        <label className="text-sm text-slate-500">Agreed Price</label>
                        <p className="text-2xl font-bold text-emerald-600">{formatCurrency(caseData.offer_price)}</p>
                      </div>
                    </div>
                    {/* Agency Fee - Only visible to staff and sellers */}
                    {caseData.sales_fee && canSeeSalesFee && (
                      <div className="mt-4 p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                        <label className="text-sm text-emerald-700 font-medium">Agency Fee</label>
                        <p className="text-xl font-bold text-emerald-800">£{Number(caseData.sales_fee).toLocaleString()}</p>
                        {caseData.offer_price && (
                          <p className="text-xs text-emerald-600 mt-1">
                            {((caseData.sales_fee / caseData.offer_price) * 100).toFixed(2)}% of sale price
                          </p>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Key Dates Card */}
                <Card>
                  <CardContent className="p-6">
                    <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-blue-600" />
                      Key Dates
                    </h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="p-3 border rounded-lg">
                        <p className="text-xs text-slate-500 mb-1">Instruction Date</p>
                        <p className="font-medium">{caseData.instruction_date?.split('T')[0] || '-'}</p>
                      </div>
                      <div className="p-3 border rounded-lg">
                        <p className="text-xs text-slate-500 mb-1">Offer Accepted</p>
                        <p className="font-medium">{caseData.offer_accepted_date?.split('T')[0] || '-'}</p>
                      </div>
                      <div className="p-3 border rounded-lg">
                        <p className="text-xs text-slate-500 mb-1">Target Exchange</p>
                        <p className="font-medium">{caseData.target_exchange_date?.split('T')[0] || '-'}</p>
                      </div>
                      <div className="p-3 border rounded-lg">
                        <p className="text-xs text-slate-500 mb-1">Target Completion</p>
                        <p className="font-medium">{caseData.target_completion_date?.split('T')[0] || '-'}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Parties Section */}
            {activeSection === "parties" && (
              <div className="space-y-6">
                {/* Seller */}
                <Card>
                  <CardContent className="p-6">
                    <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <User className="w-5 h-5 text-blue-600" />
                      Seller
                    </h2>
                    {seller ? (
                      <div className="space-y-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <User className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium">{seller.name}</p>
                            <p className="text-sm text-slate-500">{seller.email}</p>
                          </div>
                        </div>
                        {seller.phone && <p className="text-sm text-slate-600">📞 {seller.phone}</p>}
                      </div>
                    ) : (
                      <p className="text-slate-500">No seller information</p>
                    )}
                  </CardContent>
                </Card>

                {/* Seller Solicitor */}
                <Card>
                  <CardContent className="p-6">
                    <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <Briefcase className="w-5 h-5 text-indigo-600" />
                      Seller's Solicitor
                    </h2>
                    {sellerSolicitor ? (
                      <div className="space-y-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                            <Briefcase className="w-5 h-5 text-indigo-600" />
                          </div>
                          <div>
                            <p className="font-medium">{sellerSolicitor.firm_name || sellerSolicitor.name}</p>
                            <p className="text-sm text-slate-500">{sellerSolicitor.email}</p>
                          </div>
                        </div>
                        {sellerSolicitor.phone && <p className="text-sm text-slate-600">📞 {sellerSolicitor.phone}</p>}
                      </div>
                    ) : (
                      <p className="text-slate-500">No solicitor assigned</p>
                    )}
                  </CardContent>
                </Card>

                {/* Buyer */}
                <Card>
                  <CardContent className="p-6">
                    <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <User className="w-5 h-5 text-emerald-600" />
                      Buyer
                    </h2>
                    {buyer ? (
                      <div className="space-y-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                            <User className="w-5 h-5 text-emerald-600" />
                          </div>
                          <div>
                            <p className="font-medium">{buyer.name}</p>
                            <p className="text-sm text-slate-500">{buyer.email}</p>
                          </div>
                        </div>
                        {buyer.phone && <p className="text-sm text-slate-600">📞 {buyer.phone}</p>}
                      </div>
                    ) : (
                      <p className="text-slate-500">No buyer information</p>
                    )}
                  </CardContent>
                </Card>

                {/* Buyer Solicitor */}
                <Card>
                  <CardContent className="p-6">
                    <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <Briefcase className="w-5 h-5 text-purple-600" />
                      Buyer's Solicitor
                    </h2>
                    {buyerSolicitor ? (
                      <div className="space-y-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                            <Briefcase className="w-5 h-5 text-purple-600" />
                          </div>
                          <div>
                            <p className="font-medium">{buyerSolicitor.firm_name || buyerSolicitor.name}</p>
                            <p className="text-sm text-slate-500">{buyerSolicitor.email}</p>
                          </div>
                        </div>
                        {buyerSolicitor.phone && <p className="text-sm text-slate-600">📞 {buyerSolicitor.phone}</p>}
                      </div>
                    ) : (
                      <p className="text-slate-500">No solicitor assigned</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Documents Section */}
            {activeSection === "documents" && (
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-blue-600" />
                    Documents
                  </h2>
                  <div className="text-center py-8">
                    <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">Documents are available in Sales Progression</p>
                    <Button 
                      className="mt-4"
                      onClick={() => navigate(`/sales/case/${caseId}/hub`)}
                    >
                      Go to Sales Progression
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Dashboard Section */}
            {activeSection === "dashboard" && (
              <div className="space-y-6">
                <Card>
                  <CardContent className="p-6">
                    <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <Layout className="w-5 h-5 text-blue-600" />
                      Property Dashboard
                    </h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="p-4 bg-emerald-50 rounded-lg text-center">
                        <p className="text-2xl font-bold text-emerald-700">{formatCurrency(caseData.offer_price || caseData.asking_price)}</p>
                        <p className="text-sm text-emerald-600">Price</p>
                      </div>
                      <div className="p-4 bg-blue-50 rounded-lg text-center">
                        <p className="text-2xl font-bold text-blue-700 capitalize">{caseData.status || 'Active'}</p>
                        <p className="text-sm text-blue-600">Status</p>
                      </div>
                      <div className="p-4 bg-purple-50 rounded-lg text-center">
                        <p className="text-2xl font-bold text-purple-700">{caseData.bedrooms || '-'}</p>
                        <p className="text-sm text-purple-600">Bedrooms</p>
                      </div>
                      <div className="p-4 bg-amber-50 rounded-lg text-center">
                        <p className="text-lg font-bold text-amber-700">{caseData.property_type || '-'}</p>
                        <p className="text-sm text-amber-600">Type</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <div className="grid grid-cols-2 gap-6">
                  <Card>
                    <CardContent className="p-6">
                      <h3 className="font-semibold mb-3">Seller</h3>
                      {seller ? (
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <User className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium">{seller.name}</p>
                            <p className="text-sm text-slate-500">{seller.email}</p>
                          </div>
                        </div>
                      ) : (
                        <p className="text-slate-400">No seller assigned</p>
                      )}
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardContent className="p-6">
                      <h3 className="font-semibold mb-3">Buyer</h3>
                      {buyer ? (
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                            <User className="w-5 h-5 text-emerald-600" />
                          </div>
                          <div>
                            <p className="font-medium">{buyer.name}</p>
                            <p className="text-sm text-slate-500">{buyer.email}</p>
                          </div>
                        </div>
                      ) : (
                        <p className="text-slate-400">No buyer assigned</p>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}

            {/* Marketing Details Section */}
            {activeSection === "marketing" && (
              <div className="space-y-6">
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-lg font-semibold flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-blue-600" />
                        Marketing Details
                      </h2>
                      <Button onClick={saveMarketingDetails} disabled={saving} data-testid="save-marketing-btn">
                        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                        Save All Changes
                      </Button>
                    </div>
                    
                    {/* On the Market Toggle */}
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg mb-6">
                      <div>
                        <Label className="text-base font-medium">On the Market</Label>
                        <p className="text-sm text-slate-500">Property is actively listed for sale</p>
                      </div>
                      <Switch
                        checked={marketingData.on_market}
                        onCheckedChange={(checked) => setMarketingData(prev => ({ ...prev, on_market: checked }))}
                        data-testid="on-market-toggle"
                      />
                    </div>
                    
                    {/* Summary */}
                    <div className="mb-6">
                      <Label>Summary</Label>
                      <Textarea
                        value={marketingData.summary_description}
                        onChange={(e) => setMarketingData(prev => ({ ...prev, summary_description: e.target.value.slice(0, 300) }))}
                        placeholder="A stunning 3 bedroom semi-detached property..."
                        className="mt-2"
                        rows={3}
                        data-testid="summary-input"
                      />
                      <p className="text-xs text-slate-400 mt-1">
                        {marketingData.summary_description.length}/300 characters. 
                        <span className="text-slate-500 ml-1">This appears in portal search results. Keep it punchy and highlight key selling points.</span>
                      </p>
                    </div>
                    
                    {/* Full Description */}
                    <div className="mb-6">
                      <Label>Full Description (Rightmove)</Label>
                      <Textarea
                        value={marketingData.full_description}
                        onChange={(e) => setMarketingData(prev => ({ ...prev, full_description: e.target.value }))}
                        placeholder="The complete property description that appears on portal listing pages. Be detailed and descriptive."
                        className="mt-2"
                        rows={8}
                        data-testid="full-description-input"
                      />
                      <p className="text-xs text-slate-400 mt-1">{marketingData.full_description.length} characters</p>
                    </div>

                    {/* Headline */}
                    <div className="mb-6">
                      <Label>Headline</Label>
                      <Input
                        value={marketingData.headline}
                        onChange={(e) => setMarketingData(prev => ({ ...prev, headline: e.target.value }))}
                        placeholder="e.g. Stunning Family Home"
                        className="mt-2"
                        data-testid="headline-input"
                      />
                    </div>

                    {/* Marketing Tag */}
                    <div className="mb-6">
                      <Label>Marketing Tag</Label>
                      <Input
                        value={marketingData.marketing_tag}
                        onChange={(e) => setMarketingData(prev => ({ ...prev, marketing_tag: e.target.value }))}
                        placeholder="e.g. Chain Free, Must See"
                        className="mt-2"
                        data-testid="marketing-tag-input"
                      />
                    </div>

                    {/* Virtual Tour Links */}
                    <div className="mb-6">
                      <Label>Virtual Tour Links</Label>
                      <div className="space-y-2 mt-2">
                        <Input
                          value={marketingData.virtual_tour_1}
                          onChange={(e) => setMarketingData(prev => ({ ...prev, virtual_tour_1: e.target.value }))}
                          placeholder="Virtual tour link"
                          data-testid="virtual-tour-1-input"
                        />
                        <Input
                          value={marketingData.virtual_tour_2}
                          onChange={(e) => setMarketingData(prev => ({ ...prev, virtual_tour_2: e.target.value }))}
                          placeholder="Virtual tour link 2"
                          data-testid="virtual-tour-2-input"
                        />
                      </div>
                    </div>

                    {/* Website Link */}
                    <div className="mb-6">
                      <Label>Link on Website</Label>
                      <Input
                        value={marketingData.website_link}
                        onChange={(e) => setMarketingData(prev => ({ ...prev, website_link: e.target.value }))}
                        placeholder="https://yourwebsite.com/properties/..."
                        className="mt-2"
                        data-testid="website-link-input"
                      />
                    </div>

                    {/* Portal Links */}
                    <div className="mb-6">
                      <Label className="mb-3 block">Portal Links</Label>
                      <div className="space-y-3">
                        <div className="flex items-center gap-3">
                          <div className="w-32 text-sm font-medium text-slate-600 flex items-center gap-2">
                            <Globe className="w-4 h-4" /> Rightmove
                          </div>
                          <Input
                            value={marketingData.rightmove_link}
                            onChange={(e) => setMarketingData(prev => ({ ...prev, rightmove_link: e.target.value }))}
                            placeholder="https://www.rightmove.com/properties/..."
                            className="flex-1"
                            data-testid="rightmove-link-input"
                          />
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-32 text-sm font-medium text-slate-600 flex items-center gap-2">
                            <Globe className="w-4 h-4" /> Zoopla
                          </div>
                          <Input
                            value={marketingData.zoopla_link}
                            onChange={(e) => setMarketingData(prev => ({ ...prev, zoopla_link: e.target.value }))}
                            placeholder="https://www.zoopla.co.uk/..."
                            className="flex-1"
                            data-testid="zoopla-link-input"
                          />
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-32 text-sm font-medium text-slate-600 flex items-center gap-2">
                            <Globe className="w-4 h-4" /> OnTheMarket
                          </div>
                          <Input
                            value={marketingData.onthemarket_link}
                            onChange={(e) => setMarketingData(prev => ({ ...prev, onthemarket_link: e.target.value }))}
                            placeholder="https://www.onthemarket.com/details/..."
                            className="flex-1"
                            data-testid="onthemarket-link-input"
                          />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Key Features */}
                <Card>
                  <CardContent className="p-6">
                    <h3 className="font-semibold mb-4">Key Features</h3>
                    <div className="flex gap-2 mb-3">
                      <Input
                        value={newFeature}
                        onChange={(e) => setNewFeature(e.target.value)}
                        placeholder="Add a feature (e.g. 4 Bedroom detached)"
                        onKeyDown={(e) => e.key === 'Enter' && addFeature()}
                        data-testid="new-feature-input"
                      />
                      <Button onClick={addFeature} variant="outline" data-testid="add-feature-btn">
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    {marketingData.key_features.length === 0 ? (
                      <p className="text-sm text-slate-400">No features added yet. Add key selling points for portal listings.</p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {marketingData.key_features.map((feature, idx) => (
                          <Badge key={idx} variant="secondary" className="flex items-center gap-1 py-1.5 px-3">
                            {feature}
                            <X className="w-3 h-3 ml-1 cursor-pointer hover:text-red-500" onClick={() => removeFeature(idx)} />
                          </Badge>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Restrictions */}
                <Card>
                  <CardContent className="p-6">
                    <h3 className="font-semibold mb-4">Restrictions</h3>
                    <div className="flex gap-2 mb-3">
                      <Input
                        value={newRestriction}
                        onChange={(e) => setNewRestriction(e.target.value)}
                        placeholder="Add a restriction (e.g. No pets allowed)"
                        onKeyDown={(e) => e.key === 'Enter' && addRestriction()}
                        data-testid="new-restriction-input"
                      />
                      <Button onClick={addRestriction} variant="outline" data-testid="add-restriction-btn">
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    {marketingData.restrictions.length === 0 ? (
                      <p className="text-sm text-slate-400">No restrictions. Click edit to add some.</p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {marketingData.restrictions.map((restriction, idx) => (
                          <Badge key={idx} variant="destructive" className="flex items-center gap-1 py-1.5 px-3 bg-red-100 text-red-700">
                            <AlertCircle className="w-3 h-3" />
                            {restriction}
                            <X className="w-3 h-3 ml-1 cursor-pointer hover:text-red-900" onClick={() => removeRestriction(idx)} />
                          </Badge>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Utilities */}
                <Card>
                  <CardContent className="p-6">
                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                      <Zap className="w-5 h-5 text-amber-500" />
                      Utilities
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="flex items-center gap-2"><Zap className="w-4 h-4" /> Electricity Supply</Label>
                        <Input
                          value={marketingData.electricity_supply}
                          onChange={(e) => setMarketingData(prev => ({ ...prev, electricity_supply: e.target.value }))}
                          placeholder="e.g. Mains electricity"
                          className="mt-1"
                          data-testid="electricity-input"
                        />
                      </div>
                      <div>
                        <Label className="flex items-center gap-2"><Droplets className="w-4 h-4" /> Water Supply</Label>
                        <Input
                          value={marketingData.water_supply}
                          onChange={(e) => setMarketingData(prev => ({ ...prev, water_supply: e.target.value }))}
                          placeholder="e.g. Mains water"
                          className="mt-1"
                          data-testid="water-input"
                        />
                      </div>
                      <div>
                        <Label className="flex items-center gap-2"><Wifi className="w-4 h-4" /> Broadband</Label>
                        <Input
                          value={marketingData.broadband}
                          onChange={(e) => setMarketingData(prev => ({ ...prev, broadband: e.target.value }))}
                          placeholder="e.g. Fibre available, up to 100Mbps"
                          className="mt-1"
                          data-testid="broadband-input"
                        />
                      </div>
                      <div>
                        <Label className="flex items-center gap-2"><Flame className="w-4 h-4" /> Heating</Label>
                        <Input
                          value={marketingData.heating}
                          onChange={(e) => setMarketingData(prev => ({ ...prev, heating: e.target.value }))}
                          placeholder="e.g. Gas central heating"
                          className="mt-1"
                          data-testid="heating-input"
                        />
                      </div>
                      <div className="col-span-2">
                        <Label>Sewerage</Label>
                        <Input
                          value={marketingData.sewerage}
                          onChange={(e) => setMarketingData(prev => ({ ...prev, sewerage: e.target.value }))}
                          placeholder="e.g. Mains sewerage"
                          className="mt-1"
                          data-testid="sewerage-input"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Photos Section */}
            {activeSection === "photos" && (
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold flex items-center gap-2">
                      <Image className="w-5 h-5 text-blue-600" />
                      Photos
                    </h2>
                    <Button onClick={() => photoInputRef.current?.click()} disabled={uploading} data-testid="upload-photos-btn">
                      {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                      Upload Photos
                    </Button>
                    <input
                      ref={photoInputRef}
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      className="hidden"
                    />
                  </div>
                  
                  {photos.length === 0 ? (
                    <div 
                      className="border-2 border-dashed border-slate-200 rounded-lg p-12 text-center cursor-pointer hover:border-blue-400 transition-colors"
                      onClick={() => photoInputRef.current?.click()}
                    >
                      <Camera className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-500 font-medium">Drop photos here or click to upload</p>
                      <p className="text-sm text-slate-400 mt-2">Supports JPG, PNG, WebP</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 gap-4">
                      {photos.map((photo, idx) => (
                        <div key={idx} className="relative group aspect-video bg-slate-100 rounded-lg overflow-hidden">
                          <img src={photo.url || photo} alt={`Property photo ${idx + 1}`} className="w-full h-full object-cover" />
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <Button variant="destructive" size="sm" onClick={() => deletePhoto(photo.url || photo, idx)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                          {idx === 0 && (
                            <Badge className="absolute top-2 left-2 bg-blue-600">Main Photo</Badge>
                          )}
                        </div>
                      ))}
                      <div 
                        className="aspect-video border-2 border-dashed border-slate-200 rounded-lg flex items-center justify-center cursor-pointer hover:border-blue-400 transition-colors"
                        onClick={() => photoInputRef.current?.click()}
                      >
                        <div className="text-center">
                          <Plus className="w-8 h-8 text-slate-300 mx-auto" />
                          <p className="text-sm text-slate-400 mt-1">Add More</p>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Floorplans Section */}
            {activeSection === "floorplans" && (
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold flex items-center gap-2">
                      <Layout className="w-5 h-5 text-blue-600" />
                      Floorplans
                    </h2>
                    <Button onClick={() => floorplanInputRef.current?.click()} disabled={uploading} data-testid="upload-floorplans-btn">
                      {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                      Upload Floorplan
                    </Button>
                    <input
                      ref={floorplanInputRef}
                      type="file"
                      multiple
                      accept="image/*,.pdf"
                      onChange={handleFloorplanUpload}
                      className="hidden"
                    />
                  </div>
                  
                  {floorplans.length === 0 ? (
                    <div 
                      className="border-2 border-dashed border-slate-200 rounded-lg p-12 text-center cursor-pointer hover:border-blue-400 transition-colors"
                      onClick={() => floorplanInputRef.current?.click()}
                    >
                      <Layout className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-500 font-medium">Drop floorplans here or click to upload</p>
                      <p className="text-sm text-slate-400 mt-2">Supports JPG, PNG, PDF</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-4">
                      {floorplans.map((floorplan, idx) => (
                        <div key={idx} className="relative group aspect-[4/3] bg-slate-100 rounded-lg overflow-hidden">
                          <img src={floorplan.url || floorplan} alt={`Floorplan ${idx + 1}`} className="w-full h-full object-contain" />
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <Button variant="destructive" size="sm" onClick={() => deleteFloorplan(floorplan.url || floorplan, idx)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                      <div 
                        className="aspect-[4/3] border-2 border-dashed border-slate-200 rounded-lg flex items-center justify-center cursor-pointer hover:border-blue-400 transition-colors"
                        onClick={() => floorplanInputRef.current?.click()}
                      >
                        <div className="text-center">
                          <Plus className="w-8 h-8 text-slate-300 mx-auto" />
                          <p className="text-sm text-slate-400 mt-1">Add Floorplan</p>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* EPC Section */}
            {activeSection === "epc" && (
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold flex items-center gap-2">
                      <Zap className="w-5 h-5 text-emerald-600" />
                      Energy Performance Certificate (EPC)
                    </h2>
                    <Button onClick={() => epcInputRef.current?.click()} disabled={uploading} variant="outline" data-testid="upload-epc-btn">
                      {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                      Upload EPC
                    </Button>
                    <input
                      ref={epcInputRef}
                      type="file"
                      accept="image/*,.pdf"
                      onChange={handleEpcUpload}
                      className="hidden"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-6 mb-6">
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <label className="text-sm text-slate-500">EPC Rating</label>
                      <p className="text-3xl font-bold text-emerald-600 mt-1">{caseData.epc_rating || '-'}</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <label className="text-sm text-slate-500">EPC Expiry</label>
                      <p className="text-lg font-medium mt-1">{caseData.epc_expiry?.split('T')[0] || 'Not set'}</p>
                    </div>
                  </div>
                  
                  {/* EPC Document */}
                  {caseData.epc_document ? (
                    <div className="border rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                            <FileText className="w-5 h-5 text-emerald-600" />
                          </div>
                          <div>
                            <p className="font-medium">EPC Certificate</p>
                            <p className="text-sm text-slate-500">Uploaded document</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" asChild>
                            <a href={caseData.epc_document.url || caseData.epc_document} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="w-4 h-4 mr-1" /> View
                            </a>
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div 
                      className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center cursor-pointer hover:border-emerald-400 transition-colors"
                      onClick={() => epcInputRef.current?.click()}
                    >
                      <Zap className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500">No EPC document uploaded</p>
                      <p className="text-sm text-slate-400 mt-1">Click to upload EPC certificate (PDF or image)</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* IDs Section */}
            {activeSection === "ids" && (
              <div className="space-y-6">
                <Card>
                  <CardContent className="p-6">
                    <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <Key className="w-5 h-5 text-blue-600" />
                      Property References
                    </h2>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-slate-50 rounded-lg">
                        <label className="text-sm text-slate-500">Case Reference</label>
                        <p className="font-mono text-lg">{caseData.case_id}</p>
                      </div>
                      {caseData.property_id && (
                        <div className="p-4 bg-slate-50 rounded-lg">
                          <label className="text-sm text-slate-500">Property ID</label>
                          <p className="font-mono text-lg">{caseData.property_id}</p>
                        </div>
                      )}
                      {caseData.rightmove_id && (
                        <div className="p-4 bg-slate-50 rounded-lg">
                          <label className="text-sm text-slate-500">Rightmove ID</label>
                          <p className="font-mono text-lg">{caseData.rightmove_id}</p>
                        </div>
                      )}
                      {caseData.zoopla_id && (
                        <div className="p-4 bg-slate-50 rounded-lg">
                          <label className="text-sm text-slate-500">Zoopla ID</label>
                          <p className="font-mono text-lg">{caseData.zoopla_id}</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Seller ID Verification */}
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold flex items-center gap-2">
                        <User className="w-5 h-5 text-amber-600" />
                        Seller ID Verification
                      </h3>
                      <Badge className={caseData.seller_id_verified ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>
                        {caseData.seller_id_verified ? "✓ Verified" : "Pending"}
                      </Badge>
                    </div>
                    {seller ? (
                      <div className="space-y-4">
                        <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                          <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                            <User className="w-5 h-5 text-amber-600" />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium">{seller.name}</p>
                            <p className="text-sm text-slate-500">{seller.email}</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className={`p-3 rounded-lg border ${caseData.seller_photo_id ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-slate-50'}`}>
                            <div className="flex items-center gap-2">
                              {caseData.seller_photo_id ? (
                                <CheckCircle className="w-4 h-4 text-emerald-600" />
                              ) : (
                                <Circle className="w-4 h-4 text-slate-300" />
                              )}
                              <span className="text-sm">Photo ID</span>
                            </div>
                          </div>
                          <div className={`p-3 rounded-lg border ${caseData.seller_proof_of_address ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-slate-50'}`}>
                            <div className="flex items-center gap-2">
                              {caseData.seller_proof_of_address ? (
                                <CheckCircle className="w-4 h-4 text-emerald-600" />
                              ) : (
                                <Circle className="w-4 h-4 text-slate-300" />
                              )}
                              <span className="text-sm">Proof of Address</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <p className="text-slate-400">No seller assigned</p>
                    )}
                  </CardContent>
                </Card>

                {/* Buyer ID Verification */}
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold flex items-center gap-2">
                        <User className="w-5 h-5 text-blue-600" />
                        Buyer ID Verification
                      </h3>
                      <Badge className={caseData.buyer_id_verified ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>
                        {caseData.buyer_id_verified ? "✓ Verified" : "Pending"}
                      </Badge>
                    </div>
                    {buyer ? (
                      <div className="space-y-4">
                        <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <User className="w-5 h-5 text-blue-600" />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium">{buyer.name}</p>
                            <p className="text-sm text-slate-500">{buyer.email}</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className={`p-3 rounded-lg border ${caseData.buyer_photo_id ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-slate-50'}`}>
                            <div className="flex items-center gap-2">
                              {caseData.buyer_photo_id ? (
                                <CheckCircle className="w-4 h-4 text-emerald-600" />
                              ) : (
                                <Circle className="w-4 h-4 text-slate-300" />
                              )}
                              <span className="text-sm">Photo ID</span>
                            </div>
                          </div>
                          <div className={`p-3 rounded-lg border ${caseData.buyer_proof_of_address ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-slate-50'}`}>
                            <div className="flex items-center gap-2">
                              {caseData.buyer_proof_of_address ? (
                                <CheckCircle className="w-4 h-4 text-emerald-600" />
                              ) : (
                                <Circle className="w-4 h-4 text-slate-300" />
                              )}
                              <span className="text-sm">Proof of Address</span>
                            </div>
                          </div>
                          <div className={`p-3 rounded-lg border ${caseData.buyer_proof_of_funds ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-slate-50'}`}>
                            <div className="flex items-center gap-2">
                              {caseData.buyer_proof_of_funds ? (
                                <CheckCircle className="w-4 h-4 text-emerald-600" />
                              ) : (
                                <Circle className="w-4 h-4 text-slate-300" />
                              )}
                              <span className="text-sm">Proof of Funds</span>
                            </div>
                          </div>
                          <div className={`p-3 rounded-lg border ${caseData.buyer_mortgage_aip ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-slate-50'}`}>
                            <div className="flex items-center gap-2">
                              {caseData.buyer_mortgage_aip ? (
                                <CheckCircle className="w-4 h-4 text-emerald-600" />
                              ) : (
                                <Circle className="w-4 h-4 text-slate-300" />
                              )}
                              <span className="text-sm">Mortgage AIP</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <p className="text-slate-400">No buyer assigned</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Brochures Section */}
            {activeSection === "brochures" && (
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-blue-600" />
                    Brochures
                  </h2>
                  <div className="text-center py-8">
                    <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">Brochure management coming soon</p>
                    <p className="text-sm text-slate-400 mt-2">Generate and manage property brochures</p>
                    <Button variant="outline" className="mt-4" onClick={() => navigate(`/sales/case/${caseId}/brochure`)}>
                      Go to Brochure Builder
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Viewings Section */}
            {activeSection === "viewings" && (
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Eye className="w-5 h-5 text-blue-600" />
                    Viewings
                  </h2>
                  <div className="text-center py-8">
                    <Eye className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">Viewings are managed in Sales Progression</p>
                    <Button className="mt-4" onClick={() => navigate(`/sales/case/${caseId}/viewings`)}>
                      Manage Viewings
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Offers Section */}
            {activeSection === "offers" && (
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <HandCoins className="w-5 h-5 text-emerald-600" />
                    Offers
                  </h2>
                  <div className="text-center py-8">
                    <HandCoins className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">Offers are managed in Sales Progression</p>
                    <Button className="mt-4" onClick={() => navigate(`/sales/case/${caseId}/offers`)}>
                      Manage Offers
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesPropertyPage;
